import React, {useContext} from "react";
import {Button as MaterialButton} from '@mui/material';
import './Button.scss'
import {ButtonTheme} from "../../Pickers/InputTheme";
import CheckIcon from '@mui/icons-material/DoneTwoTone';
import ErrorIcon from '@mui/icons-material/ErrorTwoTone';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {ThemeProvider} from "@mui/material/styles";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {UserReactContext} from "../../../contexts/UserContext";

/**
 * Reusable Button component that wraps Material UI Button and adds WeezBee specific functionalities
 * like permission-based disabling/hiding, loading states, and action feedback.
 *
 * @param {object} params - The parameters object for the Button component.
 * @param {string} params.text - The text to display inside the button.
 * @param {function} params.onClick - The function to call when the button is clicked.
 * @param {string} [params.type] - The type of the button (e.g., 'submit', 'button').
 * @param {React.ElementType} [params.component] - The component to render the button as (e.g., 'a', Link).
 * @param {boolean} [params.disabled=false] - If true, the button will be disabled.
 * @param {React.CSSProperties} [params.style] - Custom inline styles for the button's inner div.
 * @param {string} [params.className] - Custom CSS class name for the button.
 * @param {string} [params.size=null] - The size of the button ('small', 'medium', 'large').
 * @param {React.ReactNode} [params.icon] - An icon to display within the button.
 * @param {string} [params.color="primary"] - The color of the button (e.g., 'primary', 'secondary').
 * @param {boolean} [params.iconRight=false] - If true, the icon will be displayed on the right side of the text.
 * @param {string} [params.variant="contained"] - The variant of the button ('contained', 'outlined', 'text').
 * @param {boolean} [params.loading=false] - If true, a loading spinner will be shown inside the button.
 * @param {boolean} [params.actionSucceeded] - If true, shows a success icon and text.
 * @param {string} [params.actionSucceededText] - Custom text to display when the action succeeds. Defaults to 'Done!'.
 * @param {boolean} [params.actionFailed] - If true, shows an error icon and text.
 * @param {string | string[]} [params.disableWithoutPermissions] - Feature flag(s) required to enable the button. If not met, the button is disabled.
 * @param {string | string[]} [params.hideWithoutPermissions] - Feature flag(s) required to show the button. If not met, the button is hidden.
 * @param {boolean} [params.withArrowIcon] - If true, an arrow dropdown icon is displayed next to the text.
 * @param {React.Ref<HTMLButtonElement>} [params.customRef] - A ref to attach to the underlying Material UI Button component.
 * @returns {JSX.Element} The rendered Button component.
 */
export const Button = ({
                         text, onClick, type, component, disabled, style, className, size = null,
                         icon, color = "primary", iconRight = false, variant = "contained", loading = false, actionSucceeded, actionSucceededText, actionFailed,
                         disableWithoutPermissions, hideWithoutPermissions, withArrowIcon, customRef, dataTestId
                     }) => {
    const userContext = useContext(UserReactContext)
    if (disableWithoutPermissions && !disabled)  {
        disabled = !userContext.canUserPerformAction(disableWithoutPermissions, 'and')
    }
    if (hideWithoutPermissions) {
        if (!userContext.canUserPerformAction(hideWithoutPermissions, 'and')) {
            return <></>
        }
    }
    const onClickInner = (event) => {
        if (!loading) {
            onClick(event)
        }
    }
    return <ThemeProvider
        theme={ButtonTheme({size, color})}>
        <MaterialButton
            component={component}
            className={`mui-button ${className || ''}`}
            disabled={disabled} type={type}
            onClick={onClickInner}
            variant={variant}
            ref={customRef}
            color="primary"
            data-testid={dataTestId}>
            <div className={`mui-button-inner ${size}`}  style={style} >
                {!actionSucceeded && !actionFailed ? <>
                        {icon && !iconRight && <div className="button-icon">{icon}</div>}
                        {text && <div className="button-text">{text}</div>}
                        {icon && iconRight && <div className={`button-icon ${iconRight ? 'right' : ''}`}>{icon}</div>}
                        {loading ? <LoadingSpinner color="white"/> : ''}
                        {withArrowIcon ? <div className="button-icon arrow-icon"><ArrowDropDownIcon/> </div>: ''}
                    </> :
                    <>
                        {<div className="button-icon">{actionSucceeded ? <CheckIcon/> : (actionFailed ?
                            <ErrorIcon/> : '')}</div>}
                        {text && <div
                            className="button-text">{actionSucceeded ? (actionSucceededText || 'Done!') : (actionFailed ? `Error-${text}` : '')}</div>}
                    </>}

            </div>

        </MaterialButton>
    </ThemeProvider>
}

